// MaintenanceDetailsDashboard.jsx
import React, { useMemo, useState, useCallback } from 'react';
import {
  Paper, Box, Typography, IconButton, Tooltip, Button, Snackbar, Alert,
  Accordion, AccordionSummary, AccordionDetails, Chip, Divider, TextField, InputAdornment,
  CircularProgress, Grid, Card, CardContent,
} from '@mui/material';

import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';

import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';

import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthProvider';
import { useMaintenanceDetails } from '../../../hooks/maintenance/useMaintenanceDetails';
import { useMaintenanceById } from '../../../hooks/maintenance/useMaintenances';
import AssignComponentsForm from './AssignComponentsForm';
import MaintenanceDetailEditDialog from '../utils/MaintenanceDetailEditDialog';
import DetailDocsDialog from '../utils/MaintenanceDetailDocsDialog';
import { format, parseISO } from 'date-fns';



// Toolbar compacta para cada DataGrid
function CompactToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarDensitySelector />
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
}

const formatDateTime = (val) => {
  if (!val) return '—';
  try { return format(parseISO(val), 'dd/MM/yyyy HH:mm'); } catch { return val; }
};

const chipColor = (status = '') => {
  const s = String(status).toUpperCase();
  if (s === 'FINALIZADO') return 'success';
  if (s === 'EN_PROCESO') return 'warning';
  if (s === 'CANCELADO') return 'error';
  return 'default';
};

const chipPropsForActTip = (tip = '') => {
  const s = String(tip).toUpperCase();

  if (s === 'INSPECCION') return { color: 'info', icon: <SearchRoundedIcon fontSize="small" /> };
  if (s === 'REEMPLAZO') return { color: 'primary', icon: <SwapHorizRoundedIcon fontSize="small" /> };
  if (s === 'AJUSTE') return { color: 'secondary', icon: <TuneRoundedIcon fontSize="small" /> };
  if (s === 'LIMPIEZA') return { color: 'success', icon: <CleaningServicesRoundedIcon fontSize="small" /> };
  if (s === 'LUBRICACION') return { color: 'warning', icon: <WaterDropRoundedIcon fontSize="small" /> };
  if (s === 'REPARACION') return { color: 'error', icon: <HandymanRoundedIcon fontSize="small" /> };
  // OTRO o cualquier valor no reconocido
  return { color: 'default', icon: <MoreHorizRoundedIcon fontSize="small" /> };
};

const MaintenanceDetailsDashboard = () => {
  const { vehId, manId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation(); // { vehiculo } opcional
  const vehiculo = state?.vehiculo || { vehid: Number(vehId), vehmat: `VEH-${vehId}` };
  const { user } = useAuthContext();

  const manID = Number(manId);
  const [assignOpen, setAssignOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [editState, setEditState] = useState({ open: false, row: null });
  const [docsState, setDocsState] = useState({ open: false, row: null });

  const showAlert = (message, severity = 'success') =>
    setAlert({ open: true, message, severity });

  // Trae grupos (offset mode por simplicidad; si usas cursor pasa { cursor, limit })
  const {
    data,
    groups,
    isLoading,
    isFetching,
    error,
    bulkCreateMutation,
    updateMutation,
    deleteMutation,
  } = useMaintenanceDetails(manID, { page: 1, limit: 200, estreg: 'A' });

  const { data: maintenance, isLoading: isLoadingMaintenance, errorMaintenance } = useMaintenanceById(Number(manID));

  // Mapea comid -> mandetid para eliminaciones rápidas
  const comToDetailId = useMemo(() => {
    const map = new Map();
    for (const g of groups) {
      for (const it of g.items ?? []) {
        const id = it.mandetid ?? it.manDetId;
        if (id) map.set(it.comid, id);
      }
    }
    return map;
  }, [groups]);

  // Set de comids ya asociados (para precargar el selector)
  const associatedIds = useMemo(() => {
    const s = new Set();
    for (const g of groups) for (const it of g.items ?? []) s.add(it.comid);
    return s;
  }, [groups]);

  // Búsqueda local por nombre de componente / tipo / estado
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map(g => ({
        ...g,
        items: (g.items ?? []).filter(it => {
          const name = (it.componentenombre ?? '').toLowerCase();
          const tipo = (it.tipocomponentenombre ?? '').toLowerCase();
          const est = (it.mandetest ?? it.manDetEst ?? '').toLowerCase();
          return name.includes(q) || tipo.includes(q) || est.includes(q);
        })
      }))
      .filter(g => (g.items?.length ?? 0) > 0);
  }, [groups, search]);

  // Acciones por fila
  const handleEditDetail = (row) => {
    showAlert(`Editar detalle ${row.mandetid ?? row.manDetId} (pendiente)`, 'info');
    setEditState({ open: true, row });
  };
  const closeEdit = () => setEditState({ open: false, row: null });

  //const handleDocs = (row) => 
  const handleDocs = (row) => {
    setDocsState({ open: true, row });
  };

  const handlePeople = (row) => showAlert('Asignación de personal (pendiente)', 'info');

  // Eliminar un detalle por comid
  const removeByComId = useCallback(async (comid) => {
    const detailId = comToDetailId.get(comid);
    if (!detailId) return;
    await deleteMutation.mutateAsync({ detailId, updbyid: user?.EmpID });
  }, [comToDetailId, deleteMutation, user]);

  // Columns DataGrid
  const columns = useMemo(() => ([
    {
      field: 'comid',
      headerName: 'ComID',
      width: 84,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'componentenombre',
      headerName: 'Componente',
      flex: 1.2,
      minWidth: 220,
    },
    {
      field: 'tipocomponentenombre',
      headerName: 'Tipo',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'mandetest',
      headerName: 'Estado',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      valueGetter: (p) => p.row.mandetest ?? p.row.manDetEst ?? 'PENDIENTE',
      renderCell: (params) => {
        const v = params.row.mandetest ?? params.row.manDetEst ?? 'PENDIENTE';
        return <Chip size="small" label={v} color={chipColor(v)} />;
      },
    },
    {
      field: 'acttip',
      headerName: 'Actividad',
      width: 170,
      minWidth: 160,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (p) => p.row.acttip ?? p.row.actTip ?? 'OTRO',
      renderCell: (params) => {
        const val = params.row.acttip ?? params.row.actTip ?? 'OTRO';
        const { color, icon } = chipPropsForActTip(val);
        return <Chip size="small" label={String(val).toUpperCase()} color={color} icon={icon} />;
      },
    },
    {
      field: 'mandetfecini',
      headerName: 'Inicio',
      width: 160,
      valueGetter: (p) => p.row.mandetfecini ?? p.row.manDetFecIni ?? null,
      valueFormatter: ({ value }) => (value ? formatDateTime(value) : '—'),
    },
    {
      field: 'costos',
      headerName: 'Costo Total',
      width: 130,
      headerAlign: 'right',
      align: 'right',
      valueGetter: (p) => {
        const a = Number(p.row.mandetcostoparts || 0);
        const b = Number(p.row.mandetcostomano || 0);
        const c = Number(p.row.mandetcostootro || 0);
        return a + b + c;
      },
      valueFormatter: ({ value }) => (Number.isFinite(value) ? value.toFixed(2) : '—'),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 190,
      getActions: (params) => {
        const row = params.row;

        const actions = [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon color="primary" />}
            label="Editar"
            onClick={() => handleEditDetail(row)}
            showInMenu={false}
          />,
          <GridActionsCellItem
            key="docs"
            icon={<DescriptionIcon />}
            label="Documentos"
            onClick={() => handleDocs(row)}
            showInMenu={false}
          />,
          <GridActionsCellItem
            icon={<GroupIcon />}
            label="Personal"
            onClick={() => handlePeople(row)}
            showInMenu={false}
          />,
        ]

        if (["PENDIENTE", "EN_PROCESO"].includes(row.mandetest)) {
          actions.push(
            <GridActionsCellItem
              key="remove"
              icon={<DeleteIcon color="error" />}
              label="Quitar"
              onClick={async () => {
                try {
                  if (!user?.EmpID) return showAlert('Debe iniciar sesión', 'error');
                  await removeByComId(row.comid);
                  showAlert('Componente quitado');
                } catch (e) {
                  showAlert(e?.response?.data?.error || 'Error al quitar', 'error');
                }
              }}
              showInMenu={false}
            />
          );
        }

        return actions;        
      },
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
  ]), [handleEditDetail, handleDocs, handlePeople, removeByComId, user?.EmpID]);

  // Bulk: sincronzar selección del form con lo actual (add + remove)
  const handleAssignSubmit = useCallback(async (selectedIds) => {
    if (!user?.EmpID) {
      showAlert('Debe iniciar sesión', 'error');
      return;
    }

    const current = new Set(associatedIds);
    const next = new Set(selectedIds ?? []);
    const toAdd = selectedIds.filter(id => !current.has(id));
    const toRemove = Array.from(current).filter(id => !next.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      showAlert('No hay cambios en la selección', 'info');
      return;
    }

    try {
      // 1) Agregar nuevos (bulk)
      let createdCount = 0;
      let reactivatedCount = 0;
      if (toAdd.length > 0) {
        const res = await bulkCreateMutation.mutateAsync({
          comids: toAdd,
          defaults: {},                  // puedes fijar acttip/mandetest/etc. si lo deseas
          crebyid: user.EmpID,
        });
        createdCount = res?.createdCount ?? 0;
        reactivatedCount = res?.reactivatedCount ?? 0;
      }

      // 2) Quitar los que ya no están
      let removedCount = 0;
      if (toRemove.length > 0) {
        await Promise.all(toRemove.map(removeByComId));
        removedCount = toRemove.length;
      }

      setAssignOpen(false);
      showAlert(
        `Agregados: ${createdCount}, reactivados: ${reactivatedCount}, removidos: ${removedCount}`,
        'success'
      );
    } catch (e) {
      showAlert(e?.response?.data?.error || 'Error al actualizar la selección', 'error');
    }
  }, [user, associatedIds, bulkCreateMutation, removeByComId]);

  const totalItems = useMemo(
    () => groups.reduce((acc, g) => acc + (g.items?.length ?? 0), 0),
    [groups]
  );

  const getMaintenanceStatusChipColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'CREADO': return 'info';
      case 'INICIADO': return 'primary';
      case 'EN_PROCESO': return 'warning';
      case 'FINALIZADO': return 'success';
      case 'CANCELADO': return 'error';
      default: return 'default';
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Detalle de Mantenimiento</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <TextField
            size="small"
            placeholder="Buscar componente / tipo / estado…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
          />
          <Tooltip title="Refrescar">
            <span>
              <IconButton onClick={() => window.location.reload()} disabled={isFetching || isLoading}>
                {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Gestionar componentes">
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAssignOpen(true)}
                disabled={isLoading || bulkCreateMutation.isPending || deleteMutation.isPending}
              >
                Gestionar
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Información General del Mantenimiento */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12}> {/* La tarjeta principal ocupa todo el ancho */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>Información General del Mantenimiento</Typography>
              <Divider sx={{ mb: 2 }} /> {/* Más espacio después del título */}

              <Grid container spacing={2}> {/* Nuevo Grid para las dos columnas internas */}
                <Grid item xs={12} sm={6}> {/* Primera columna: Detalles clave */}
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>ID Mantenimiento:</strong> {manID}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Tipo:</strong> {maintenance?.tipomantenimientonombre || 'N/A'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Estado:</strong> <Chip label={maintenance?.manest} color={getMaintenanceStatusChipColor(maintenance?.manest)} size="small" sx={{ ml: 1 }} />
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Fecha Inicio:</strong> {formatDateTime(maintenance?.manfecini)}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Fecha Fin:</strong> {formatDateTime(maintenance?.manfecfin)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Vehículo:</strong> {maintenance?.vehiculomatricula || 'N/A'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Kilometraje:</strong> {maintenance?.mankm ? `${maintenance?.mankm} km` : 'N/A'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Tiempo Fuera:</strong> {maintenance?.mantiefuehor ? `${maintenance?.mantiefuehor} horas` : 'N/A'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Costo Total:</strong> {maintenance?.costototal ? `$${parseFloat(maintenance?.costototal).toFixed(2)}` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}> {/* Fila para Observaciones (ocupará todo el ancho) */}
                  <Typography variant="body1">
                    <strong>Observaciones:</strong> {maintenance?.manobs || 'Sin observaciones.'}
                  </Typography>
                </Grid>
                <Grid item xs={12}> {/* Fila para Auditoría (ocupará todo el ancho) */}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" display="block" color="text.secondary">
                    Creado el: {formatDateTime(maintenance?.credat)} por {maintenance?.crebyid}
                  </Typography>
                  {maintenance?.updbyid && maintenance?.upddat && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Última actualización: {formatDateTime(maintenance?.upddat)} por {maintenance?.updbyid}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Resumen vehículo / totales */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle2" color="text.secondary">
          Sistemas: <strong>{groups.length}</strong> · Componentes: <strong>{totalItems}</strong>
        </Typography>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error?.message || 'Error al cargar detalles.'}</Alert>
      ) : filteredGroups.length === 0 ? (
        <Alert severity="info">No hay componentes asociados a este mantenimiento.</Alert>
      ) : (
        filteredGroups.map(group => {
          const rows = (group.items ?? []).map(r => ({ id: r.mandetid ?? r.manDetId, ...r }));
          return (
            <Accordion key={`grp-${group.sisvehid}`} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{group.sistemanombre ?? 'Sin sistema'}</Typography>
                <Chip size="small" label={rows.length} sx={{ ml: 2 }} />
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 1 }} />
                <div style={{ width: '100%' }}>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    autoHeight
                    disableRowSelectionOnClick
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10, page: 0 } },
                    }}
                    slots={{ toolbar: CompactToolbar }}
                    loading={isFetching}
                  />
                </div>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      {/* Selector de componentes */}
      {assignOpen && (
        <AssignComponentsForm
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          vehiculo={vehiculo}
          defaultSelectedIds={Array.from(associatedIds)}
          onSubmit={handleAssignSubmit}
        />
      )}

      {/* Editar Por componente */}
      {editState.open && (
        <MaintenanceDetailEditDialog
          open={editState.open}
          row={editState.row}
          onClose={closeEdit}
          isSaving={updateMutation.isPending}
          onSubmit={async (diff) => {
            if (diff?._noChanges) {
              showAlert('No hay cambios que guardar', 'info');
              closeEdit();
              return;
            }
            try {
              const detailId = editState.row.mandetid ?? editState.row.manDetId;
              // payload: diff + updbyid
              const payload = { ...diff, updbyid: user?.EmpID };
              await updateMutation.mutateAsync({ detailId, payload });
              showAlert('Detalle actualizado');
              closeEdit();
            } catch (e) {
              showAlert(e?.response?.data?.error || 'Error al actualizar', 'error');
            }
          }}
        />
      )}

      {/* Añadir documento al detalle */}
      {docsState.open && (
        <DetailDocsDialog
          open={docsState.open}
          onClose={() => setDocsState({ open: false, row: null })}
          manId={manID}
          row={docsState.row}
          apiBase={process.env.REACT_APP_API_URL}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={3500}
        onClose={() => setAlert(a => ({ ...a, open: false }))}
      >
        <Alert severity={alert.severity} onClose={() => setAlert(a => ({ ...a, open: false }))}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default MaintenanceDetailsDashboard;
